import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminSeminarApplicationDetail,
} from '../app/yowanContentClient';

const content = {
  activeNavHref: '/admin/events/seminars',
  routeKey: '/admin/events/seminars/applications/:applicationId',
  header: {
    title: 'セミナー申込詳細',
    paragraphs: [
      'セミナー申込の内容、申込時点の入力、同一メールアドレスの観測履歴を確認します。',
    ],
  },
};

const ReadOnlyField = ({ label, value, multiline = false, rows = 3 }) => {
  const safeValue = value || '-';

  return (
    <label className={`c-form__field yowan-admin-readonly-field${multiline ? ' yowan-admin-readonly-field--wide' : ''}`}>
      <span className="c-form__label">{label}</span>
      {multiline ? (
        <textarea
          className="c-form__control c-form__control--textarea"
          readOnly
          rows={rows}
          value={safeValue}
        />
      ) : (
        <input className="c-form__control" readOnly type="text" value={safeValue} />
      )}
    </label>
  );
};

const memberStatusStyles = {
  member: {
    color: '#188647',
    background: 'rgba(24, 134, 71, 0.1)',
    border: 'rgba(24, 134, 71, 0.34)',
  },
  candidate: {
    color: '#8a6400',
    background: 'rgba(198, 147, 25, 0.12)',
    border: 'rgba(198, 147, 25, 0.36)',
  },
  none: {
    color: '#65706b',
    background: 'rgba(101, 112, 107, 0.08)',
    border: 'rgba(101, 112, 107, 0.22)',
  },
};

const MemberStatusIndicator = ({ status, preservedDomainSearch }) => {
  const safeStatus = status || {};
  const tone = memberStatusStyles[safeStatus.variant] || memberStatusStyles.none;

  return (
    <div
      className="yowan-admin-readonly-field"
      style={{
        display: 'grid',
        gap: '0.35rem',
        alignContent: 'start',
        padding: '0.7rem 0.8rem',
        border: `1px solid ${tone.border}`,
        background: tone.background,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.45rem',
          color: tone.color,
          fontWeight: 700,
          lineHeight: 1.4,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: '0.72rem',
            height: '0.72rem',
            borderRadius: '999px',
            background: tone.color,
          }}
        />
        {safeStatus.label || '会員紐づきなし'}
      </span>
      <span className="yowan-admin-detail-panel__meta">
        {safeStatus.description || 'この申込に会員紐づきはありません。'}
        {safeStatus.href ? (
          <>
            {' '}
            <Link to={appendYowanPreviewQueryIfNeeded(safeStatus.href, preservedDomainSearch)}>
              会員詳細
            </Link>
          </>
        ) : null}
      </span>
    </div>
  );
};

const DetailSection = ({ title, lead, children }) => (
  <section className="yowan-admin-detail-panel yowan-class-application-detail__section">
    <header className="yowan-class-application-detail__section-header">
      <h2 className="yowan-admin-detail-panel__title">{title}</h2>
      {lead ? <p className="yowan-admin-detail-panel__lead">{lead}</p> : null}
    </header>
    {children}
  </section>
);

const IdentityObservationList = ({ observations }) => {
  if (!observations.length) {
    return (
      <p className="yowan-admin-detail-panel__meta">
        同一メールアドレスで異なる入力内容はまだ観測されていません。
      </p>
    );
  }

  return (
    <div className="yowan-class-application-detail__observations">
      {observations.map((observation, index) => (
        <article className="yowan-class-application-detail__observation" key={observation.id || index}>
          <div>
            <p className="yowan-class-application-detail__observation-title">
              {observation.observedName || observation.observedEmail || `観測履歴 ${index + 1}`}
            </p>
            <p className="yowan-admin-detail-panel__meta">
              {[
                observation.observedAt,
                observation.sourceLabel,
                observation.changedFieldsLabel ? `差分: ${observation.changedFieldsLabel}` : '',
              ].filter(Boolean).join(' / ')}
            </p>
          </div>
          <dl className="yowan-class-application-detail__observation-grid">
            <div>
              <dt>ふりがな</dt>
              <dd>{observation.observedNameKana || '-'}</dd>
            </div>
            <div>
              <dt>メールアドレス</dt>
              <dd>{observation.observedEmail || '-'}</dd>
            </div>
            <div>
              <dt>電話番号</dt>
              <dd>{observation.observedPhone || '-'}</dd>
            </div>
            <div>
              <dt>メモ</dt>
              <dd>{observation.note || '-'}</dd>
            </div>
          </dl>
        </article>
      ))}
    </div>
  );
};

const AdminSeminarApplicationDetailPage = () => {
  const preservedDomainSearch = usePreservedDomainSearch();
  const { applicationId } = useParams();
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminSeminarApplicationDetail(applicationId)
      .then((payload) => {
        if (isMounted) {
          setRecord(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecord(null);
          setLoadError('セミナー申込詳細を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [applicationId]);

  const listHref = record?.eventId
    ? `/admin/events/${record.eventId}/applications`
    : '/admin/events/seminars';
  const directMemberLink = record?.directMemberLink || {};
  const currentApplicantDifferences = record?.currentApplicantDifferences || [];

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={record ? `${record.applicantName || '申込者'} セミナー申込詳細` : content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="applications-list" to={appendYowanPreviewQueryIfNeeded(listHref, preservedDomainSearch)}>
            参加者一覧
          </Link>,
          <Link key="seminars-list" to={appendYowanPreviewQueryIfNeeded('/admin/events/seminars', preservedDomainSearch)}>
            セミナー一覧
          </Link>,
          record?.applicantHref ? (
            <Link key="applicant-detail" to={appendYowanPreviewQueryIfNeeded(record.applicantHref, preservedDomainSearch)}>
              問い合わせ・申込者詳細
            </Link>
          ) : null,
          directMemberLink.href ? (
            <Link key="member-detail" to={appendYowanPreviewQueryIfNeeded(directMemberLink.href, preservedDomainSearch)}>
              会員詳細
            </Link>
          ) : null,
        ]}
      />

      <div className="yowan-admin-form c-surface-content">
        {isLoading ? <LoadingSpinner label="セミナー申込詳細を読み込み中" showLabel /> : null}
        {!isLoading && loadError ? (
          <p className="yowan-admin-form__message">{loadError}</p>
        ) : null}

        {!isLoading && !loadError && record ? (
          <div className="yowan-class-application-detail">
            <DetailSection
              title="申込者情報"
              lead="セミナー申込時に保存された内容を表示します。"
            >
              <div className="yowan-class-application-detail__grid">
                <MemberStatusIndicator
                  status={record.memberStatus}
                  preservedDomainSearch={preservedDomainSearch}
                />
                <ReadOnlyField label="氏名" value={record.applicantName} />
                <ReadOnlyField label="ふりがな" value={record.applicantNameKana} />
                <ReadOnlyField label="メールアドレス" value={record.applicantEmail} />
                <ReadOnlyField label="電話番号" value={record.applicantPhone} />
                <ReadOnlyField label="申込メッセージ" value={record.message} multiline rows={5} />
              </div>
            </DetailSection>

            {currentApplicantDifferences.length ? (
              <DetailSection
                title="問い合わせ・申込者登録値との差分"
                lead="申込時点の入力内容と、現在の問い合わせ・申込者登録値が異なる項目だけ表示します。"
              >
                <div className="yowan-class-application-detail__grid">
                  {currentApplicantDifferences.map((row) => (
                    <React.Fragment key={row.field}>
                      <ReadOnlyField label={`${row.label}（申込時点）`} value={row.submittedValue} />
                      <ReadOnlyField label={`${row.label}（現在）`} value={row.currentValue} />
                    </React.Fragment>
                  ))}
                </div>
              </DetailSection>
            ) : null}

            <DetailSection title="申込内容">
              <div className="yowan-class-application-detail__grid">
                <ReadOnlyField label="セミナー" value={record.eventTitle || record.eventSeries?.title} />
                <ReadOnlyField label="シリーズ" value={record.eventSeries?.title} />
                <ReadOnlyField label="参加範囲" value={record.participationScopeLabel} />
                <ReadOnlyField label="参加するコマ" value={record.slotParticipationLabel} multiline rows={4} />
                <ReadOnlyField label="参加費" value={record.slotFeeLabel} />
                <ReadOnlyField label="状態" value={record.statusLabel} />
                <ReadOnlyField label="申込日" value={record.createdAt} />
                <ReadOnlyField label="更新日" value={record.updatedAt} />
              </div>
            </DetailSection>

            <DetailSection
              title="同一メールアドレスの観測履歴"
              lead="同一メールアドレスで別名・別ふりがな・別電話番号などが観測された場合に表示します。"
            >
              <IdentityObservationList observations={record.identityObservations || []} />
            </DetailSection>
          </div>
        ) : null}
        {!isLoading && !loadError && !record ? (
          <p className="yowan-admin-form__message">申込詳細はまだ取得できません。</p>
        ) : null}
      </div>
    </AdminBlogShell>
  );
};

export default AdminSeminarApplicationDetailPage;
